<template>
  <Shell class="store-shell"
    :nav="nav"
    link-groups
    :active="shop.categoryId ?? 'all'"
    theme="light"
    brand-suffix="pood"
    :note="vatNote">
    <template #breadcrumb>
      <template v-for="(crumb, i) in crumbs"
        :key="i">
        <span v-if="i"
          class="sep">/</span>
        <span v-if="i === crumbs.length - 1"
          class="current"
          aria-current="page">{{ crumb.label }}</span>
        <RouterLink v-else
          :to="crumb.to">{{ crumb.label }}</RouterLink>
      </template>
    </template>

    <!-- A signed-in admin sees who they are instead of the VAT note, which stays in the footer. -->
    <template v-if="auth.isLoggedIn"
      #sidebar-footer="{ rail }">
      <SidebarUser :rail="rail"
        to="/admin"
        title="Haldus" />
    </template>

    <template #topbar-right>
      <button class="tb-btn tb-docs"
        type="button"
        @click="shop.offerOpen = !shop.offerOpen">
        <i class="fa-solid fa-file-lines"
          aria-hidden="true" />
        Päring ({{ shop.offerCount }})
      </button>
    </template>

    <ShopFilter v-if="$route.name === 'shop' || $route.name === 'shop-category'" />
    <router-view />

    <!-- Offer inquiry panel -->
    <Transition name="slide">
      <div v-if="shop.offerOpen"
        class="offer-overlay"
        @click.self="shop.offerOpen = false">
        <div class="offer-panel">
          <div class="offer-panel__header">
            <h3>Pakkumise päring</h3>
            <button class="offer-panel__close"
              @click="shop.offerOpen = false">
              <i class="fa-solid fa-xmark" />
            </button>
          </div>
          <div v-if="!shop.offerCount"
            class="offer-panel__empty">
            <i class="fa-solid fa-box-open" />
            <p>Päringus pole veel tooteid. Lisa tooteid nupuga "Päring".</p>
          </div>
          <ContactForm v-else
            :products="shop.offerProducts"
            @remove-product="shop.removeFromOffer($event)" />
        </div>
      </div>
    </Transition>

    <template #footer>
      <!-- Takes over from the sidebar note when the sidebar is a rail or a drawer, or shows the user. -->
      <div class="footer-cell"
        :class="{ 'footer-cell--note': !auth.isLoggedIn }">{{ vatNote }}</div>
      <div class="footer-cell">&copy; {{ new Date().getFullYear() }} Aktuaalne OÜ.<br>Kõik õigused
        kaitstud.</div>
      <div class="footer-cell">Registrikood: 11376530<br>KMKR number: EE101214928</div>
      <div class="footer-cell">
        <!-- One flex item, so the <br> stacks the links. -->
        <div class="footer-contact">
          <a href="mailto:info@aktuaalne.ee">info@aktuaalne.ee</a><br>
          <a href="tel:+37253311191">+372 5331 1191</a>
        </div>
      </div>
      <div class="footer-cell">
        <a href="https://www.facebook.com/profile.php?id=100063649654196"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Facebook"><i class="fa-brands fa-facebook-f" /></a>
      </div>
    </template>
  </Shell>
</template>

<script>
import { useShopStore } from '@/stores/shop'
import { useAuthStore } from '@/stores/auth'
import ShopFilter from './shop/filter.vue'
import ContactForm from '@/components/ContactForm.vue'
import SidebarUser from '@/components/SidebarUser.vue'
import Shell from '@/layouts/Shell.vue'

const categoryPath = (c) => `/pood/${c.slug || c.id}`

// Search, brand, price range, spec filters and page live in the URL (?q=…&brand=…&min=…&max=…&s12=205|215&lk=…),
// so Back, reload and a shared link bring the same list back.
const listQuery = (query) => ({
  search: String(query.q || ''),
  brandId: query.brand ? Number(query.brand) : null,
  priceMin: Number(query.min) > 0 ? Number(query.min) : null,
  priceMax: Number(query.max) > 0 ? Number(query.max) : null,
  specs: Object.fromEntries(Object.entries(query)
    .filter(([k, v]) => /^s\d+$/.test(k) && v)
    .map(([k, v]) => [k.slice(1), String(v).split('|').filter(Boolean)])),
  page: Math.max(1, Number(query.lk) || 1),
})
const VAT_NOTE = 'Hinnad sisaldavad käibemaksu.'

export default {
  name: 'shop',
  components: { ShopFilter, ContactForm, SidebarUser, Shell },
  data() {
    return {
      shop: useShopStore(),
      auth: useAuthStore(),
      vatNote: VAT_NOTE,
    }
  },
  computed: {
    // Category links keep the search, brand and price range; the page starts over.
    keep() {
      const { q, brand, min, max } = this.$route.query
      return { ...(q ? { q } : {}), ...(brand ? { brand } : {}), ...(min ? { min } : {}), ...(max ? { max } : {}) }
    },
    nav() {
      const to = (path) => ({ path, query: this.keep })
      const items = [{ id: 'all', label: 'Kõik tooted', icon: 'fa-solid fa-box-open', to: to('/pood') }]
      for (const c of this.shop.categories.filter((c) => !c.parentId)) {
        const item = { id: c.id, label: c.name, icon: c.icon || 'fa-solid fa-tag', to: to(categoryPath(c)) }
        // From the loaded (active) list, the one syncFromRoute resolves against.
        const children = this.shop.categories
          .filter((ch) => ch.parentId === c.id)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        if (children.length) {
          item.children = children.map((ch) => ({ id: ch.id, label: ch.name, to: to(categoryPath(ch)) }))
        }
        items.push(item)
      }
      return items
    },
    activeCategory() {
      if (!this.shop.categoryId && this.shop.selectedProduct?.category) {
        return this.shop.categories.find(c => c.id === this.shop.selectedProduct.category.id)
          || this.shop.selectedProduct.category
      }
      if (!this.shop.categoryId) return null
      return this.shop.categories.find(c => c.id === this.shop.categoryId)
    },
    parentCategory() {
      if (!this.activeCategory?.parentId) return null
      return this.shop.categories.find(c => c.id === this.activeCategory.parentId)
    },
    crumbs() {
      const crumbs = [{ label: 'Tooted', to: '/pood' }]
      if (this.parentCategory) crumbs.push({ label: this.parentCategory.name, to: categoryPath(this.parentCategory) })
      if (this.activeCategory) crumbs.push({ label: this.activeCategory.name, to: categoryPath(this.activeCategory) })
      if (this.shop.selectedProduct) crumbs.push({ label: this.shop.selectedProduct.name })
      return crumbs
    },
  },
  methods: {
    // The route is the source of truth for the category; the nav and the
    // breadcrumb are plain links.
    syncFromRoute() {
      if (this.$route.name === 'product') return
      this.shop.clearSelection()
      Object.assign(this.shop, listQuery(this.$route.query))
      if (!this.shop.categories.length) return
      const slug = this.$route.params.category
      // categoryPath falls back to the id for a category without a slug.
      this.shop.categoryId = (slug && this.shop.categories.find(c => c.slug === slug || String(c.id) === slug)?.id) ?? null
      if (this.shop.listKey() !== this.shop.loadedKey) this.shop.loadProducts()
    },
  },
  watch: {
    $route: 'syncFromRoute',
    'shop.categories': 'syncFromRoute',
  },
  mounted() {
    // The URL's search and page first, so the first load already uses them.
    if (this.$route.name !== 'product') Object.assign(this.shop, listQuery(this.$route.query))
    if (!this.shop.products.length) this.shop.load()
    this.syncFromRoute()
  }
}
</script>

<style lang="scss" scoped>
.footer-cell {
  flex: 1 1 25%;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 16rem;

  & + & {
    border-left: 1px solid var(--border-color-light);
  }

  // gentelella's breakpoints, not the app's, so this follows the rail/drawer.
  @media (max-width: 768px) {
    flex-basis: 50%;
    padding: 8rem 12rem;

    &:nth-child(odd) {
      border-left: none;
    }
  }
}

// .footer outranks admin-theme's `.admin-shell .main a` (--primary, near-black in the shop), which ties otherwise.
.footer .footer-contact a {
  color: inherit;
}

@media (min-width: 769px) {
  body:not(.sidebar-rail) .footer-cell--note {
    display: none;
  }

  body:not(.sidebar-rail) .footer-cell--note + .footer-cell {
    border-left: none;
  }
}

/* Offer panel */
.offer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.offer-panel {
  width: 400rem;
  max-width: 90vw;
  background: #fff;
  height: 100%;
  overflow-y: auto;
  padding: 24rem;
  display: flex;
  flex-direction: column;
  gap: 20rem;
}

.offer-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    font-size: 18rem;
    font-weight: 700;
    color: var(--primary);
  }
}

.offer-panel__close {
  background: none;
  border: none;
  font-size: 20rem;
  cursor: pointer;
  color: #666;
  padding: 4rem;
}

.offer-panel__empty {
  text-align: center;
  padding: 40rem 20rem;
  color: #999;

  i {
    font-size: 40rem;
    margin-bottom: 12rem;
  }

  p {
    font-size: 14rem;
    line-height: 1.5;
  }
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;

  .offer-panel {
    transform: translateX(100%);
  }
}
</style>
